/**
 * Anexo: o que o canal aceita, e como se recusa (C27, H16, §8.2).
 *
 * ── Por que a REGRA mora no contrato, e não no domínio ────────────────────
 *
 * Porque ela precisa rodar nos dois lados, e a fronteira só deixa um pacote
 * atravessar. A regra `r3-web-so-usa-o-cliente-gerado` do dependency-cruiser é
 * explícita: `apps/web` importa `packages/contracts` e mais nada.
 *
 * E os dois lados precisam MESMO da regra:
 *
 *   · a tela recusa ANTES de subir — a H16 diz que é *"a diferença entre a
 *     pessoa saber na hora e esperar trinta segundos de upload para ouvir
 *     não"*;
 *   · o servidor recusa de novo, porque validação de navegador não é barreira.
 *
 * Duas implementações da mesma regra divergem — e a divergência aqui tem uma
 * cara específica e ruim: a tela aceita, o servidor recusa, e a pessoa conclui
 * que o sistema está quebrado. Uma função só, chamada dos dois lugares.
 *
 * ── A engine NÃO valida, e é isso que decide o desenho ────────────────────
 *
 * A A03 §8 mediu nos quatro canais: *"Anexo: validação da engine — nenhuma"*.
 * O `aplicadoPelaEngine` é `false` em tudo que não é `web_widget`. O Chatwoot
 * aceita upload de 90 MB, responde 2xx, e a **Meta recusa depois** — com a
 * mensagem já registrada como enviada do nosso lado.
 *
 * Então a barreira é nossa. Não há a quem delegar.
 *
 * ── De onde vêm os números ────────────────────────────────────────────────
 *
 * De `capabilities`, que a C03 projeta por inbox — nunca de constante aqui.
 * A A03 mediu as regras do `@chatwoot/utils` da imagem em execução, e elas
 * mudam com a versão da engine e com o que a Meta publica. Número no código
 * seria número errado no dia em que o WhatsApp mudasse o teto.
 */

import { z } from 'zod';

/**
 * As categorias que o Chatwoot usa para achar o teto de tamanho.
 *
 * `application` e `text` colapsam em `document` — está na `resolucao` do
 * payload medido, e é por isso que a categoria não é simplesmente o prefixo
 * do MIME. Um PDF (`application/pdf`) e um TXT (`text/plain`) disputam o mesmo
 * limite, que no WhatsApp é 100 MB.
 */
export const CATEGORIAS_DE_ANEXO = ['image', 'video', 'audio', 'document'] as const;
export type CategoriaDeAnexo = (typeof CATEGORIAS_DE_ANEXO)[number];

/** A categoria de um MIME, pela regra que a engine usa. */
export function categoriaDoMime(mime: string): CategoriaDeAnexo {
  const prefixo = mime.split('/')[0]?.toLowerCase() ?? '';
  if (prefixo === 'image') return 'image';
  if (prefixo === 'video') return 'video';
  if (prefixo === 'audio') return 'audio';
  // Tudo o mais é documento: `application/*`, `text/*` e o que aparecer.
  return 'document';
}

/**
 * Os limites do canal, do jeito que `capabilities.limites.anexo` os entrega.
 *
 * Campos anuláveis porque a matriz pode não dizer, e "não disse" nunca vira
 * permissão nem proibição sozinho — quem decide isso é cada conferência
 * abaixo, explicitamente.
 */
export interface LimitesDeAnexo {
  /** `null` = o canal não tem regra de tipo; aceita o que a engine aceitar. */
  readonly mimeAceitos: readonly string[] | null;
  readonly maxMbPorCategoria: Readonly<Record<string, number>>;
  readonly quantidadeMaxima: number | null;
}

/** Só o que a conferência precisa saber de um arquivo. */
export interface AnexoParaConferir {
  readonly nome: string;
  readonly mime: string;
  readonly bytes: number;
}

/**
 * Uma recusa, pronta para virar mensagem.
 *
 * O texto vem junto porque a §8.2 exige que ação indisponível venha SEMPRE com
 * explicação: *"'Fora da janela de 24 h — use um template aprovado' ensina;
 * botão cinza sem motivo gera chamado no suporte."* Devolver só um código
 * obrigaria cada chamador a escrever a frase, e aí a tela e o servidor
 * explicariam a mesma recusa com palavras diferentes.
 */
export interface RecusaDeAnexo {
  readonly campo: 'tipo' | 'tamanho' | 'quantidade';
  /** O arquivo que causou a recusa. `null` quando a recusa é do conjunto. */
  readonly nome: string | null;
  readonly motivo: string;
}

const UM_MB = 1024 * 1024;

/** `image/*` casa com `image/png`. É como a tabela da engine é escrita. */
function mimeCasa(aceito: string, mime: string): boolean {
  const alvo = mime.toLowerCase();
  const padrao = aceito.toLowerCase();
  if (padrao.endsWith('/*')) return alvo.startsWith(padrao.slice(0, -1));
  return padrao === alvo;
}

/**
 * Confere um conjunto de anexos contra o canal. `null` = pode enviar.
 *
 * ── Por que o conjunto, e não um por vez ──────────────────────────────────
 *
 * Porque `quantidadeMaxima` só é decidível olhando todos. Conferir um a um e
 * somar depois deixaria a contagem em quem chama — e "em quem chama" é onde
 * ela seria esquecida num dos dois lados.
 *
 * Devolve a PRIMEIRA recusa, não todas. Quem está anexando resolve um problema
 * por vez, e uma lista de cinco recusas de uma vez esconde qual atacar
 * primeiro.
 */
export function conferirAnexos(
  anexos: readonly AnexoParaConferir[],
  limites: LimitesDeAnexo,
  canal: string,
): RecusaDeAnexo | null {
  if (anexos.length === 0) return null;

  if (limites.quantidadeMaxima !== null && anexos.length > limites.quantidadeMaxima) {
    return {
      campo: 'quantidade',
      nome: null,
      motivo:
        `São ${anexos.length} arquivos; o canal ${canal} aceita no máximo ` +
        `${limites.quantidadeMaxima} por mensagem.`,
    };
  }

  for (const anexo of anexos) {
    /*
     * `mimeAceitos: null` passa de propósito.
     *
     * É o caso do Telegram, que o Chatwoot não configura — a A03 registrou:
     * *"não tem nó de configuração; cai no default: tudo, 40 MB"*. Recusar
     * tudo aqui quebraria um canal que funciona; o tamanho continua valendo.
     */
    if (limites.mimeAceitos !== null) {
      const aceito = limites.mimeAceitos.some((padrao) => mimeCasa(padrao, anexo.mime));
      if (!aceito) {
        return {
          campo: 'tipo',
          nome: anexo.nome,
          motivo:
            `O canal ${canal} não aceita arquivos do tipo ${anexo.mime}. ` +
            `Ele aceita: ${resumirTipos(limites.mimeAceitos, anexo.mime)}.`,
        };
      }
    }

    const categoria = categoriaDoMime(anexo.mime);
    const maxMb = limites.maxMbPorCategoria[categoria] ?? limites.maxMbPorCategoria['default'];
    if (maxMb === undefined) continue;

    if (anexo.bytes > maxMb * UM_MB) {
      return {
        campo: 'tamanho',
        nome: anexo.nome,
        motivo:
          `"${anexo.nome}" tem ${emMb(anexo.bytes)} MB; o canal ${canal} aceita até ` +
          `${maxMb} MB para ${nomeDaCategoria(categoria)}.`,
      };
    }
  }

  return null;
}

/** Uma casa decimal: "4.7 MB" informa, "4.7382 MB" só ocupa espaço. */
function emMb(bytes: number): string {
  return (bytes / UM_MB).toFixed(1);
}

const NOME_DA_CATEGORIA: Readonly<Record<CategoriaDeAnexo, string>> = {
  image: 'imagem',
  video: 'vídeo',
  audio: 'áudio',
  document: 'documento',
};

function nomeDaCategoria(categoria: CategoriaDeAnexo): string {
  return NOME_DA_CATEGORIA[categoria];
}

/**
 * O que dizer que o canal aceita, para quem acabou de ser recusado.
 *
 * ── Por que não é só a lista de categorias ───────────────────────────────
 *
 * Era, e a mensagem se contradizia. Recusar um `image/webp` produzia *"não
 * aceita image/webp. Ele aceita: áudio, imagem, vídeo, texto, documento"* — o
 * canal aceita imagem E recusa a sua imagem, sem dizer que a diferença está no
 * formato. Quem leu isso em 02/09/2026 entendeu que o WhatsApp só aceitava
 * áudio, e parou de tentar.
 *
 * Agora, quando a recusa é de um tipo cuja CATEGORIA o canal aceita, a
 * mensagem mostra os formatos daquela categoria — que é a informação que
 * resolve: "converta para JPEG ou PNG". Uma lista crua de MIME não serviria
 * (`application/vnd.openxmlformats-officedocument.spreadsheetml.sheet` não
 * ajuda ninguém), então o formato aparece pelo sufixo, em maiúsculas.
 *
 * Quando a categoria inteira é recusada — PDF num canal sem documento — a
 * resposta certa continua sendo a lista de categorias, porque não há formato
 * que salve.
 */
function resumirTipos(mimes: readonly string[], recusado?: string): string {
  const categoriaRecusada = recusado === undefined ? undefined : categoriaDoMime(recusado);

  if (categoriaRecusada !== undefined) {
    const daMesmaCategoria = mimes.filter((m) => categoriaDoMime(m) === categoriaRecusada);
    if (daMesmaCategoria.length > 0) {
      const formatos = [...new Set(daMesmaCategoria.map(formatoLegivel))];
      return `${nomeDaCategoria(categoriaRecusada)} nos formatos ${formatos.join(', ')}`;
    }
  }

  const porCategoria = new Set<string>();
  for (const mime of mimes) porCategoria.add(nomeDaCategoria(categoriaDoMime(mime)));
  return [...porCategoria].join(', ');
}

/** `image/jpeg` → `JPEG`. O sufixo é o que a pessoa reconhece como "formato". */
function formatoLegivel(mime: string): string {
  const sufixo = mime.split('/')[1] ?? mime;
  // `vnd.openxmlformats-...presentationml.presentation` vira `PRESENTATION`,
  // que é ruim e ainda assim melhor que a linha inteira.
  const ultimo = sufixo.split(/[.+-]/).pop() ?? sufixo;
  return ultimo.toUpperCase();
}

/**
 * O que o navegador manda junto do arquivo, para a conferência do servidor.
 *
 * O arquivo em si vai no corpo `multipart`; isto é o cabeçalho dele. Existe
 * separado porque a conferência de tipo e tamanho precisa acontecer ANTES de
 * ler o corpo inteiro — que é o ponto da task: recusar sem gastar o upload.
 */
export const AnexoParaEnvioSchema = z
  .object({
    nome: z.string().min(1).max(255),
    mime: z.string().min(3).max(255),
    bytes: z.number().int().positive(),
  })
  .strict();

export type AnexoParaEnvio = z.infer<typeof AnexoParaEnvioSchema>;

/**
 * O anexo na resposta — o mesmo DTO que a leitura da C02 devolve.
 *
 * ── A URL NUNCA é a do Chatwoot ───────────────────────────────────────────
 *
 * O `data_url` do ActiveStorage não expira e não exige token. O DTO da C02 já
 * registra a consequência: *"vazamento de payload de webhook é, por isso,
 * vazamento de arquivo"*. Aqui vai a URL assinada pelo adapter, com validade
 * curta — e `expiraEm` viaja junto para a tela poder avisar em vez de mostrar
 * um link que morre calado.
 */
export const AnexoSchema = z
  .object({
    id: z.string().min(1),
    /*
     * A categoria que o motor gravou (`image`, `file`, `audio`...), e não o
     * MIME cheio.
     *
     * É o que ele devolve, e inventar um `nome` aqui seria pior do que a
     * ausência: o Chatwoot não repete o nome original do arquivo na resposta,
     * então o campo viria vazio ou preenchido com um palpite — e a tela
     * mostraria o palpite como se fosse o nome do documento que a pessoa
     * acabou de enviar.
     */
    tipoArquivo: z.string(),
    extensao: z.string().nullable(),
    bytes: z.number().int().nonnegative(),
    url: z.string(),
    expiraEm: z.string().datetime(),
  })
  .strict();

export type Anexo = z.infer<typeof AnexoSchema>;
